/**
 * <PasswordGate> — système "site en construction" + accès privé caché.
 *
 * COMPORTEMENT
 * ────────────
 * Si la variable d'env `REACT_APP_SITE_PASSWORD` est vide → bypass total
 * (site 100% public).
 *
 * Sinon :
 *   • PUBLIC (visiteur lambda)
 *       → voit la page "En construction" branded Energy Market.
 *       → ne sait pas qu'il y a un site derrière.
 *
 *   • INSIDER avec un lien magique
 *       URL = https://energie-market.fr/?preview=<mot_de_passe>
 *       → le composant détecte le query param, déverrouille la session,
 *         nettoie l'URL et affiche directement le vrai site.
 *
 *   • INSIDER qui connaît le mot de passe
 *       → clique sur le tout petit lien "Accès partenaire" en bas à droite
 *         de la page En construction.
 *       → saisit le mot de passe → déverrouille la session.
 *
 * Le déverrouillage est stocké dans sessionStorage (réinitialisé à la
 * fermeture du navigateur).
 *
 * ⚠️ Sécurité : le mot de passe est dans le bundle JS (visible si on inspecte).
 * C'est volontairement une barrière "early access", pas un vrai contrôle d'accès.
 * Pour de la vraie sécurité (avant le go public), utilise en plus Vercel Pro
 * "Password Protection" ou Cloudflare Access (gratuit jusqu'à 50 users).
 */
import { useState, useEffect } from "react";
import { Lock, ArrowRight, Zap, ShieldCheck } from "lucide-react";

const STORAGE_KEY = "em_gate_ok";
const QUERY_PARAM = "preview";

export default function PasswordGate({ children }) {
    const expected = process.env.REACT_APP_SITE_PASSWORD || "";
    const [unlocked, setUnlocked] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [input, setInput] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        // 1. Pas de mot de passe configuré → site public, bypass
        if (!expected) {
            setUnlocked(true);
            return;
        }
        // 2. Déjà déverrouillé dans cette session → bypass
        if (sessionStorage.getItem(STORAGE_KEY) === "ok") {
            setUnlocked(true);
            return;
        }
        // 3. URL contient ?preview=<password> → déverrouille + clean URL
        const url = new URL(window.location.href);
        const token = url.searchParams.get(QUERY_PARAM);
        if (token && token === expected) {
            sessionStorage.setItem(STORAGE_KEY, "ok");
            url.searchParams.delete(QUERY_PARAM);
            window.history.replaceState({}, "", url.toString());
            setUnlocked(true);
        }
    }, [expected]);

    const submit = (e) => {
        e.preventDefault();
        if (input === expected) {
            sessionStorage.setItem(STORAGE_KEY, "ok");
            setUnlocked(true);
            setError("");
            setShowPasswordModal(false);
        } else {
            setError("Mot de passe incorrect");
            setInput("");
        }
    };

    if (unlocked) return children;

    // ──────────────────────────────────────────────────────────────────
    // PAGE "EN CONSTRUCTION" PUBLIQUE
    // ──────────────────────────────────────────────────────────────────
    return (
        <>
            <div className="min-h-screen bg-gradient-to-br from-[#0A3B2A] via-[#0A3B2A] to-[#072719] text-white relative overflow-hidden">
                {/* Décor : cercles flous en arrière-plan */}
                <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
                    <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#B8E04A] rounded-full blur-3xl opacity-15" />
                    <div className="absolute -bottom-40 -left-32 w-[500px] h-[500px] bg-[#2E7D62] rounded-full blur-3xl opacity-20" />
                </div>

                <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-16 text-center">

                    {/* Badge "Bientôt" */}
                    <div className="inline-flex items-center gap-2 bg-[#B8E04A]/15 text-[#B8E04A] px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.2em] mb-8 backdrop-blur ring-1 ring-[#B8E04A]/30">
                        <span className="w-2 h-2 rounded-full bg-[#B8E04A] animate-pulse" />
                        Lancement imminent
                    </div>

                    {/* Logo / titre */}
                    <div className="flex items-center gap-3 mb-2">
                        <div className="h-12 w-12 rounded-2xl bg-[#B8E04A] flex items-center justify-center shadow-lg">
                            <Zap className="h-7 w-7 text-[#0A3B2A]" strokeWidth={2.5} />
                        </div>
                        <p className="font-display text-3xl font-black tracking-tight">Energy Market</p>
                    </div>
                    <p className="text-sm uppercase tracking-[0.25em] text-white/60 font-semibold">
                        Courtage énergie · rénovation
                    </p>

                    {/* Titre principal */}
                    <h1 className="font-display text-5xl md:text-7xl font-black tracking-tight mt-12 max-w-3xl leading-[1.05]">
                        Bientôt
                        <br />
                        <span className="text-[#B8E04A]">en ligne.</span>
                    </h1>

                    {/* Sous-titre */}
                    <p className="text-lg md:text-xl text-white/80 mt-8 max-w-xl leading-relaxed">
                        Nous finalisons votre nouveau comparateur énergétique : analyse de
                        factures, devis personnalisés, aides à la rénovation et
                        accompagnement complet. <strong className="text-white">Restez connectés.</strong>
                    </p>

                    {/* Stats teaser */}
                    <div className="grid grid-cols-3 gap-8 md:gap-16 mt-16 max-w-xl">
                        <div>
                            <p className="font-display text-3xl md:text-4xl font-black text-[#B8E04A]">−30%</p>
                            <p className="text-xs uppercase tracking-wider text-white/60 mt-2">Économies moyennes</p>
                        </div>
                        <div>
                            <p className="font-display text-3xl md:text-4xl font-black text-[#B8E04A]">1€</p>
                            <p className="text-xs uppercase tracking-wider text-white/60 mt-2">Isolation aides État</p>
                        </div>
                        <div>
                            <p className="font-display text-3xl md:text-4xl font-black text-[#B8E04A]">100%</p>
                            <p className="text-xs uppercase tracking-wider text-white/60 mt-2">Gratuit & transparent</p>
                        </div>
                    </div>

                    {/* Ligne de confiance */}
                    <div className="flex items-center gap-3 mt-16 text-sm text-white/50">
                        <ShieldCheck className="h-4 w-4 text-[#B8E04A]" />
                        Service indépendant · Conformité ORIAS · Données protégées RGPD
                    </div>

                    {/* Contact pendant la construction */}
                    <p className="text-sm text-white/40 mt-8">
                        Une question ?{" "}
                        <a href="mailto:contact@energymarket.fr" className="text-[#B8E04A] hover:underline">
                            contact@energymarket.fr
                        </a>
                    </p>
                </div>

                {/* Lien discret "Accès partenaire" en bas à droite */}
                <button
                    type="button"
                    onClick={() => setShowPasswordModal(true)}
                    className="fixed bottom-6 right-6 z-20 text-[11px] uppercase tracking-[0.18em] text-white/30 hover:text-[#B8E04A] transition-colors flex items-center gap-1.5 px-3 py-2"
                    aria-label="Accès partenaire"
                >
                    <Lock className="h-3 w-3" />
                    Accès privé
                </button>
            </div>

            {/* ──────────────────────────────────────────────────────────────
                MODAL MOT DE PASSE (caché par défaut)
                ────────────────────────────────────────────────────────────── */}
            {showPasswordModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) setShowPasswordModal(false); }}
                >
                    <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-col items-center text-center">
                            <div className="h-14 w-14 rounded-2xl bg-[#B8E04A] flex items-center justify-center mb-4">
                                <Lock className="h-7 w-7 text-[#0A3B2A]" />
                            </div>
                            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#2E7D62]">Energy Market</p>
                            <h2 className="font-display text-2xl font-black text-[#0A3B2A] mt-2">
                                Accès en avant-première
                            </h2>
                            <p className="text-sm text-gray-500 mt-2">
                                Saisissez le mot de passe d'accès partenaire pour découvrir le site avant son lancement public.
                            </p>
                        </div>
                        <form onSubmit={submit} className="mt-6 space-y-3">
                            <input
                                type="password"
                                autoFocus
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Mot de passe d'accès"
                                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0A3B2A] focus:border-transparent"
                            />
                            {error && (
                                <p className="text-sm text-red-600 text-center">{error}</p>
                            )}
                            <button
                                type="submit"
                                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#0A3B2A] text-white px-5 py-3 font-semibold hover:bg-[#072719] transition-colors"
                            >
                                Entrer
                                <ArrowRight className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowPasswordModal(false)}
                                className="w-full text-sm text-gray-400 hover:text-gray-600 py-2"
                            >
                                Annuler
                            </button>
                        </form>
                        <p className="text-xs text-center text-gray-400 mt-4">
                            Astuce : vous pouvez aussi utiliser un lien direct du type
                            <br />
                            <code className="text-gray-500">?preview=motdepasse</code>
                        </p>
                    </div>
                </div>
            )}
        </>
    );
}
